import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea, ScrollAreaViewport, ScrollBar } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Search, ChevronLeft, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { type CollectionCase } from "@/types/collections";
import { useCollectionCasesListGraphQL } from "@/hooks/useCollectionCasesGraphQL";
import { formatCurrency } from "@/lib/utils/formatters";
import { useCompany } from "@/contexts/CompanyContext";

const SIDEBAR_PAGE_SIZE = 25;

interface CaseDetailsSidebarProps {
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

export function CaseDetailsSidebar({ 
  isCollapsed = false,
  onToggleCollapse 
}: CaseDetailsSidebarProps) {
  const navigate = useNavigate();
  const { caseId } = useParams<{ caseId: string }>();
  const { selectedCompany } = useCompany();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [accumulatedCases, setAccumulatedCases] = useState<CollectionCase[]>([]);
  const prevDataRef = useRef<string>("");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset page and accumulated cases when search changes
  useEffect(() => {
    setPage(1);
    setAccumulatedCases([]);
    prevDataRef.current = "";
  }, [debouncedSearch]);

  // Fetch cases with pagination and optional search
  const queryParams = {
    page,
    pageSize: SIDEBAR_PAGE_SIZE,
    sortBy: "nextActionDate",
    sortOrder: "asc" as const,
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
  };

  const { data, isLoading } = useCollectionCasesListGraphQL(queryParams);
  const fetchedCases = useMemo(() => data?.cases ?? [], [data?.cases]);
  const hasNextPage = data?.pagination.hasNextPage ?? false;

  // Accumulate cases when new data arrives
  useEffect(() => {
    if (fetchedCases.length === 0) return;

    const dataKey = fetchedCases.map((c) => `${c.id}:${c.displayOutstandingAmount ?? c.baseAmount ?? 0}:${c.status}:${c.priority}:${c.customerName}:${c.daysInCollection}`).join(",");
    if (dataKey === prevDataRef.current) return;
    prevDataRef.current = dataKey;

    if (page === 1) {
      setAccumulatedCases(fetchedCases);
    } else {
      setAccumulatedCases((prev) => {
        const existingIds = new Set(prev.map((c) => c.id));
        const newCases = fetchedCases.filter((c) => !existingIds.has(c.id));
        return [...prev, ...newCases];
      });
    }
  }, [fetchedCases, page]);

  // Handle empty results for page 1
  useEffect(() => {
    if (!isLoading && fetchedCases.length === 0 && page === 1) {
      setAccumulatedCases([]);
    }
  }, [isLoading, fetchedCases.length, page]);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isLoading) {
      setPage((prev) => prev + 1);
    }
  }, [hasNextPage, isLoading]);

  const handleCaseClick = (selectedCaseId: string) => {
    navigate(`/collections/queue/case/${selectedCaseId}`, {
      state: { from: "queue" }
    });
  };

  const isCaseActive = (c: CollectionCase) =>
    c.caseId === caseId || c.id === caseId;

  const isLoadingFirstPage = isLoading && page === 1;
  const isLoadingMore = isLoading && page > 1;

  return (
    <div className="relative h-full flex flex-col">
      {!isCollapsed && (
      <Card className="h-full border-r rounded-none flex flex-col shadow-md">
        <CardHeader className="pb-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Collection Cases</CardTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggleCollapse}
              className="h-8 w-8 rounded-full hover:bg-muted"
              title="Close sidebar"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          </div>
          <div className="relative mt-2">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search cases..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-9"
            />
          </div>
        </CardHeader>
        <CardContent className="px-0 pb-0 flex-1 min-h-0">
          <ScrollArea className="h-full w-full">
            <ScrollAreaViewport className="h-full">
              <div className="space-y-1 px-3 pb-3">
                {isLoadingFirstPage && (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                )}
                {!isLoadingFirstPage && accumulatedCases.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    {searchQuery ? "No cases found" : "No cases available"}
                  </div>
                )}
                {!isLoadingFirstPage && accumulatedCases.length > 0 && (
                  <>
                    {accumulatedCases.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => handleCaseClick(c.caseId)}
                        className={cn(
                          "w-full text-left p-3 rounded-lg border-l-4 transition-all hover:bg-muted/50",
                          isCaseActive(c)
                            ? "border-l-teal-500 bg-primary/10 shadow-sm"
                            : "border-l-gray-300 dark:border-l-gray-700 bg-background hover:shadow-sm"
                        )}
                      >
                        <div className="space-y-1.5">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm truncate">
                                {c.caseNumber || c.caseId}
                              </p>
                              <p className="text-xs text-muted-foreground truncate">
                                {c.customerName}
                              </p>
                            </div>
                            <Badge
                              variant={
                                c.priority === "High"
                                  ? "destructive"
                                  : c.priority === "Medium"
                                  ? "default"
                                  : "secondary"
                              }
                              className="text-xs shrink-0"
                            >
                              {c.priority}
                            </Badge>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-bold text-red-600 dark:text-red-400">
                              {formatCurrency(c.displayOutstandingAmount ?? c.baseAmount ?? 0, c.currency || selectedCompany?.currency || "GBP")}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {c.daysInCollection}d
                            </span>
                          </div>
                        </div>
                      </button>
                    ))}
                    {hasNextPage && (
                      <button
                        onClick={handleLoadMore}
                        disabled={isLoadingMore}
                        className="w-full py-3 text-center text-sm font-medium text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 hover:bg-muted/50 rounded-lg transition-colors disabled:opacity-50"
                      >
                        {isLoadingMore ? (
                          <span className="flex items-center justify-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading...
                          </span>
                        ) : (
                          "Load more"
                        )}
                      </button>
                    )}
                  </>
                )}
              </div>
            </ScrollAreaViewport>
            <ScrollBar orientation="vertical" />
          </ScrollArea>
        </CardContent>
      </Card>
      )}
    </div>
  );
}
